import BlockRegistry from './BlockRegistry';

/**
 * @typedef {Object} BoardProfile
 * @property {string} name - e.g. "Arduino Uno"
 * @property {string} architecture - e.g. "avr", "esp32"
 */

class GeneratorContext {
    /**
     * @param {BoardProfile} board 
     * @param {Object} blocks 
     * @param {string} language 'arduino' | 'python'
     */
    constructor(board, blocks, language = 'arduino') {
        this.board = board;
        this.blocks = blocks;
        this.language = language;
        
        this.includes = new Set();
        this.globalVars = new Map();
        this.setupCode = new Set();
        this.helperFunctions = new Map();
        
        // Maps Scratch variable ID -> Unique C++ sanitized name
        this.variableRegistry = new Map();
        this.usedVarNames = new Set();
    }

    addInclude(include) {
        this.includes.add(include.trim());
    }

    addGlobal(id, code) {
        this.globalVars.set(id, code);
    }

    addSetup(code) {
        this.setupCode.add(code.trim());
    }

    addHelper(id, code) {
        this.helperFunctions.set(id, code.trim());
    }

    /**
     * Registers a Scratch variable and ensures its sanitized C++ name is completely unique.
     * @param {string} scratchId The internal scratch variable ID
     * @param {string} rawName The human-readable name of the variable
     * @returns {string} The safely sanitized, unique C++ variable name
     */
    registerVariable(scratchId, rawName) {
        if (this.variableRegistry.has(scratchId)) {
            return this.variableRegistry.get(scratchId);
        }

        let sanitized = rawName.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1');
        if (!sanitized) sanitized = 'var';

        let uniqueName = sanitized;
        let counter = 1;
        while (this.usedVarNames.has(uniqueName)) {
            uniqueName = `${sanitized}_${counter}`;
            counter++;
        }

        this.usedVarNames.add(uniqueName);
        this.variableRegistry.set(scratchId, uniqueName);
        return uniqueName;
    }

    getInputValue(block, inputName) {
        const input = block.inputs && block.inputs[inputName];
        if (!input) return '';

        const targetId = typeof input === 'string' ? input : (input.block || input[1]);

        if (Array.isArray(input) && typeof input[1] !== 'object') {
            return String(input[1]);
        }

        const targetBlock = this.blocks[targetId];
        if (targetBlock) {
            return BlockRegistry.generate(targetBlock, this);
        }
        return '';
    }

    getFieldValue(block, fieldName) {
        if (block.fields && block.fields[fieldName]) {
            return block.fields[fieldName].value;
        }
        return '';
    }

    getFieldId(block, fieldName) {
        if (block.fields && block.fields[fieldName]) {
            return block.fields[fieldName].id || block.fields[fieldName].value;
        }
        return '';
    }

    generateSubstack(block, substackName) {
        const input = block.inputs && block.inputs[substackName];
        if (!input) return '';

        const targetId = input.block || input[1];
        if (!targetId) return '';

        let currentId = targetId;
        const lines = [];

        while (currentId) {
            const b = this.blocks[currentId];
            if (!b) break;

            const code = BlockRegistry.generate(b, this);
            if (code) {
                // Ensure proper statement termination
                if (code.trim().endsWith('}') || code.trim().endsWith(';')) {
                    lines.push(code);
                } else {
                    lines.push(`${code};`);
                }
            }
            currentId = b.next;
        }

        return lines.map(line => `    ${line}`).join('\n');
    }

    getBuilderData() {
        return {
            includes: Array.from(this.includes),
            globals: Array.from(this.globalVars.values()),
            setup: Array.from(this.setupCode),
            helpers: Array.from(this.helperFunctions.values())
        };
    }
}

export default GeneratorContext;
